import {
  ISourceEntity,
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISODateTimeType,
  ISODateType,
  ISOTimeType,
  Timestamp,
  Timezone,
  TypeGuard,
  isISODateTimeType,
  isISODateType,
  isISOTimeType,
  toFloat,
  toISODate,
  toTimestamp,
} from '@principle-theorem/shared';
import { flow, isBoolean, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import {
  convertDateToTimestampFn,
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  convertValueFn,
} from '../../../source/source-helpers';
import { PATIENT_NUMBER_LIMIT } from './third-parties';

export const PATIENT_DISCOUNT_RESOURCE_TYPE = 'patientDiscounts';

export const PATIENT_DISCOUNT_SOURCE_ENTITY: ISourceEntity = SourceEntity.init({
  metadata: {
    label: 'Patient Discount List',
    description: '',
    idPrefix: PATIENT_DISCOUNT_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IOasisPatientDiscount {
  id: number;
  transactionGroupId?: string;
  appointmentDate: ISODateType;
  createdAt: ISODateType;
  amount?: number;
  description?: string;
  accountPatientId: number;
  patientName: string;
  practitionerId: number;
  billedToPractitionerId: number;
  statementNumber?: number;
  isDeleted: boolean;
  deletedAt?: ISODateTimeType;
  locationId?: number;
  userId: number;
  userTime?: ISOTimeType;
  comment?: string;
  showInGst?: boolean;
}

export function isOasisPatientDiscount(
  item: unknown,
  logError: boolean
): item is IOasisPatientDiscount {
  return TypeGuard.interface<IOasisPatientDiscount>(
    {
      id: isNumber,
      transactionGroupId: TypeGuard.nilOr(isString),
      appointmentDate: isISODateType,
      createdAt: isISODateType,
      amount: TypeGuard.nilOr(isNumber),
      description: TypeGuard.nilOr(isString),
      accountPatientId: isNumber,
      patientName: isString,
      practitionerId: isNumber,
      billedToPractitionerId: isNumber,
      statementNumber: TypeGuard.nilOr(isNumber),
      isDeleted: isBoolean,
      deletedAt: TypeGuard.nilOr(isISODateTimeType),
      locationId: TypeGuard.nilOr(isNumber),
      userId: isNumber,
      userTime: TypeGuard.nilOr(isISOTimeType),
      comment: TypeGuard.nilOr(isString),
      showInGst: TypeGuard.nilOr(isBoolean),
    },
    logError
  )(item);
}

export interface IOasisPatientDiscountTranslations {
  createdAt: Timestamp;
  appointmentDate: ISODateType;
  deletedAt?: Timestamp;
}

export interface IOasisPatientDiscountFilters {
  practitionerId: number;
  accountPatientId: number;
}

const PATIENT_DISCOUNT_SOURCE_QUERY = `
SELECT
  SEQNUMBER AS id,
  NULLIF(RUNNUMBER::TEXT, 'NULL') AS transaction_group_id,

  CASE
    WHEN convert_to_integer(ACCNUMBER) < ${PATIENT_NUMBER_LIMIT}
      THEN convert_to_integer(ACCNUMBER)
    ELSE NULL
  END AS account_patient_id,

  PATIENTNAME AS patient_name,

  normalise_oasis_date_field(CONSULTDATE) AS appointment_date,
  normalise_oasis_date_field(ENTRYDATE) AS created_at,

  convert_to_decimal(AMOUNT) AS amount,
  NULLIF(DESCRIPTION1, '') AS description,
  NULLIF(STATEMENTNUMBER, 0) AS statement_number,
  CONSULTDRNUMBER AS practitioner_id,
  BILLDRNUMBER AS billed_to_practitioner_id,

  CASE WHEN DELETED = -1
    THEN TRUE
    ELSE FALSE
  END AS is_deleted,
  to_char(
    to_date(normalise_oasis_date_field(DELETEDATE), 'YYYY-MM-DD')
    +
    CASE
      WHEN DELETETIME ~ '^\\d{1,2}:\\d{2}:\\d{2} (AM|PM)$'
        THEN NULLIF(TRIM(DELETETIME), '')::TIME
      WHEN DELETETIME ~ '^\\d{2}:\\d{2}:\\d{2}$'
        THEN NULLIF(TRIM(DELETETIME), '')::TIME
      ELSE NULL
    END,
    'YYYY-MM-DD HH24:MI:SS'
  ) AS deleted_at,

  NULLIF(COMMENT, '') AS comment,
  USERNUMBER AS user_id,
  NULLIF(NULLIF(USERTIME, ''), 'NULL')::TIME AS user_time,
  convert_to_integer(LOCNO) AS location_id,

  convert_to_boolean(SHOWINGST) AS show_in_gst
FROM PBARCMAS
WHERE
  TRANSTYPE = 5
`;

const PATIENT_DISCOUNT_SOURCE_QUERY_V2 = `
SELECT
  SEQNUMBER AS id,
  NULLIF(RUNNUMBER::TEXT, 'NULL') AS transaction_group_id,

  CASE
    WHEN convert_to_integer(ACCNUMBER) < ${PATIENT_NUMBER_LIMIT}
      THEN convert_to_integer(ACCNUMBER)
    ELSE NULL
  END AS account_patient_id,

  PATIENTNAME AS patient_name,

  normalise_oasis_date_field(CONSULTDATE) AS appointment_date,
  normalise_oasis_date_field(ENTRYDATE) AS created_at,

  convert_to_decimal(AMOUNT) AS amount,
  NULLIF(DESCRIPTION1, '') AS description,
  NULLIF(STATEMENTNUMBER, 0) AS statement_number,
  CONSULTDRNUMBER AS practitioner_id,
  BILLDRNUMBER AS billed_to_practitioner_id,

  CASE WHEN DELETED = -1
    THEN TRUE
    ELSE FALSE
  END AS is_deleted,
  CASE
    WHEN NULLIF(DELETEDATE, '') IS NULL
      THEN NULL
    ELSE to_char(
      convert_to_date(DELETEDATE, 'YYYY-MM-DD')::timestamp AT TIME ZONE 'UTC',
      'YYYY-MM-DD HH24:MI:SS'
    )
  END AS deleted_at,

  NULLIF(COMMENT, '') AS comment,
  USERNUMBER AS user_id,
  CASE
    WHEN NULLIF(USERTIME, '') IS NULL
      THEN NULL
    ELSE to_char(to_timestamp(USERTIME, 'HH24:MI:SS AM'), 'HH24:MI:SS')
  END AS user_time,
  convert_to_integer(LOCNO) AS location_id
FROM PBARCMAS
WHERE
  TRANSTYPE = 5
`;

export type OasisPatientDiscount = ISourceEntityTypes<
  IOasisPatientDiscount,
  IOasisPatientDiscountTranslations,
  IOasisPatientDiscountFilters
>;

export class PatientDiscountSourceEntity extends BaseSourceEntity<OasisPatientDiscount> {
  sourceEntity = PATIENT_DISCOUNT_SOURCE_ENTITY;
  entityResourceType = PATIENT_DISCOUNT_RESOURCE_TYPE;
  sourceQuery = [
    PATIENT_DISCOUNT_SOURCE_QUERY,
    PATIENT_DISCOUNT_SOURCE_QUERY_V2,
  ];
  verifySourceFn = isOasisPatientDiscount;
  override defaultOffsetSize = 50000;
  override transformDataFn = flow([
    convertKeysToCamelCaseFn(),
    convertNullToUndefinedFn(),
    convertDateToTimestampFn(),
    convertValueFn(toFloat, 'amount'),
  ]);

  translate(
    data: IOasisPatientDiscount,
    timezone: Timezone
  ): IOasisPatientDiscountTranslations {
    return {
      createdAt: toTimestamp(moment.tz(data.createdAt, timezone)),
      appointmentDate: toISODate(moment.tz(data.appointmentDate, timezone)),
      deletedAt: data.deletedAt
        ? toTimestamp(moment.tz(data.deletedAt, timezone))
        : undefined,
    };
  }

  getSourceRecordId(data: IOasisPatientDiscount): number {
    return data.id;
  }

  getSourceLabel(data: IOasisPatientDiscount): string {
    return data.id.toString();
  }

  getFilterData(
    data: IOasisPatientDiscount,
    _timezone: Timezone
  ): IOasisPatientDiscountFilters {
    return {
      practitionerId: data.practitionerId,
      accountPatientId: data.accountPatientId,
    };
  }
}
